import { lazy, Suspense, useEffect } from 'react';
import { loadInitialApp } from '../initial-app-recovery';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';

import { buttonClassName } from '@forma/ui';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { FullScreenLoadingOverlay } from '../features/app/full-screen-loading-overlay';
import { readStoredAppLanguage } from '../features/app/language-storage';
import { createTranslator, getInitialAppLanguage } from '../i18n';

const TrainerApp = lazy(() =>
  import('../features/trainer/trainer-app').then((module) => ({
    default: module.TrainerApp,
  })),
);

const RoutinePreviewPage = lazy(() =>
  import('../features/routine-preview/routine-preview-page').then((module) => ({
    default: module.RoutinePreviewPage,
  })),
);

const ClientApp = lazy(() => loadInitialApp(() => import('../App')));

const ExerciseDemoPage = import.meta.env.DEV
  ? lazy(() =>
      import('../features/workout/prototype/exercise-demo-page').then(
        (module) => ({ default: module.ExerciseDemoPage }),
      ),
    )
  : null;

function RouteLoadingPage() {
  const t = createTranslator(getInitialAppLanguage(readStoredAppLanguage()));

  return (
    <main className="min-h-screen">
      <FullScreenLoadingOverlay
        isVisible
        animatedEllipsis
        label={t('Loading Forma')}
      />
    </main>
  );
}

function ClientRouteApp() {
  return (
    <Suspense fallback={<RouteLoadingPage />}>
      <ClientApp />
    </Suspense>
  );
}

function TrainerRouteApp() {
  const location = useLocation();
  return (
    <Suspense fallback={<RouteLoadingPage />}>
      <TrainerApp currentPath={location.pathname} />
    </Suspense>
  );
}

function InviteRouteApp() {
  const { inviteId = '' } = useParams();
  return (
    <Suspense fallback={<RouteLoadingPage />}>
      <TrainerApp inviteId={inviteId} />
    </Suspense>
  );
}

function LegacyInviteRedirect() {
  const location = useLocation();
  const { inviteId = '' } = useParams();
  return (
    <Navigate
      replace
      to={{ pathname: `/app/invite/${inviteId}`, search: location.search }}
    />
  );
}

function LegacyWatchClaimRedirect() {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{ pathname: '/app/watch/claim', search: location.search }}
    />
  );
}

function RoutinePreviewRedirect() {
  const { adminSlug = '' } = useParams();
  return (
    <Navigate
      replace
      to={`/app/?addRoutine=${encodeURIComponent(adminSlug)}`}
    />
  );
}

function NotFoundPage() {
  const location = useLocation();
  const language = getInitialAppLanguage(readStoredAppLanguage());
  const t = createTranslator(language);
  const isTrainerRoute =
    location.pathname === '/trainer' ||
    location.pathname.startsWith('/trainer/');
  const isClientRoute =
    location.pathname === '/app' || location.pathname.startsWith('/app/');
  const destination = isTrainerRoute
    ? '/trainer'
    : isClientRoute
      ? '/app/'
      : language === 'pl'
        ? '/pl/'
        : '/';

  useEffect(() => {
    const previousTitle = document.title;
    document.documentElement.lang = language;
    document.title = `${t('Page not found')} | Forma`;

    return () => {
      document.title = previousTitle;
    };
  }, [language, t]);

  return (
    <main className="grid min-h-screen place-items-center px-4">
      <Card className="w-full max-w-lg border-white/70 bg-white/86 shadow-2xl backdrop-blur-xl">
        <CardHeader>
          <CardTitle>{t('Page not found')}</CardTitle>
          <CardDescription>
            {t('This page does not exist or may have moved.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link className={buttonClassName()} to={destination}>
            {t(isTrainerRoute ? 'Open trainer workspace' : 'Back to Forma')}
          </Link>
        </CardContent>
      </Card>
    </main>
  );
}

export function AppRouter() {
  return (
    <Routes>
      {ExerciseDemoPage && (
        <Route
          path="/app/prototype/exercise"
          element={
            <Suspense fallback={<RouteLoadingPage />}>
              <ExerciseDemoPage />
            </Suspense>
          }
        />
      )}
      <Route
        path="/app/routine-preview/:token"
        element={
          <Suspense fallback={<RouteLoadingPage />}>
            <RoutinePreviewPage />
          </Suspense>
        }
      />
      <Route path="/app" element={<ClientRouteApp />} />
      <Route
        path="/app/library/:adminSlug"
        element={<RoutinePreviewRedirect />}
      />
      <Route path="/app/routines/:routineId" element={<ClientRouteApp />} />
      <Route path="/app/watch/claim" element={<ClientRouteApp />} />
      <Route path="/app/invite/:inviteId" element={<InviteRouteApp />} />

      <Route path="/trainer" element={<TrainerRouteApp />} />
      <Route path="/trainer/plans" element={<TrainerRouteApp />} />
      <Route path="/trainer/demo" element={<TrainerRouteApp />} />
      <Route
        path="/trainer/demo/clients/:clientId"
        element={<TrainerRouteApp />}
      />
      <Route
        path="/trainer/demo/clients/:clientId/routines/:routineId"
        element={<TrainerRouteApp />}
      />
      <Route
        path="/trainer/demo/routines/:routineId"
        element={<TrainerRouteApp />}
      />
      <Route path="/trainer/clients/:clientId" element={<TrainerRouteApp />} />
      <Route
        path="/trainer/clients/:clientId/routines/:routineId/edit"
        element={<TrainerRouteApp />}
      />
      <Route
        path="/trainer/routines/:routineId/edit"
        element={<TrainerRouteApp />}
      />

      <Route path="/invite/:inviteId" element={<LegacyInviteRedirect />} />
      <Route path="/watch/claim" element={<LegacyWatchClaimRedirect />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
